import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, ChevronDown, RefreshCw, Loader2, Globe, Search, AlertCircle } from "lucide-react";
import { getCase, getRelated, startExplore, getExploration, refreshExploration, MARKETS } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/Reveal";

const STAGES = {
  understanding: "Understanding the transformation…",
  searching: "Looking for comparable solutions…",
  checking: "Checking local businesses and products…",
  reviewing: "Reviewing the evidence…",
  preparing: "Preparing what we found…",
};
const STAGE_ORDER = ["understanding", "searching", "checking", "reviewing", "preparing"];

const CLASS_ORDER = [
  "Direct Local Solution", "Related Local Solution",
  "International Solution Available Locally", "Local Implementation", "Other Relevant Evidence",
];
const CLASS_STYLE = {
  "Direct Local Solution": "bg-brlime text-ink",
  "Related Local Solution": "bg-bryellow text-ink",
  "International Solution Available Locally": "bg-cobalt text-white",
  "Local Implementation": "bg-brpink text-white",
  "Other Relevant Evidence": "bg-secondary text-ink/70",
};
const STATE_COPY = {
  STRONG: { t: "Strong local evidence", d: "We found substantial public evidence that this type of solution is already being offered in this market." },
  SOME: { t: "Some local evidence", d: "We found several related solutions, but limited evidence of businesses specialising specifically in this use case." },
  LIMITED: { t: "Limited evidence", d: "We found limited reliable public evidence of this specific type of solution in this market. This does not mean no such businesses exist — it means our search found limited public evidence." },
  INSUFFICIENT: { t: "Insufficient evidence", d: "We could not find enough reliable public information to form a useful view." },
};
const APPROACH_COPY = {
  BUILD: "Build a specialised solution.",
  INTEGRATE: "Use existing AI technology and implement it for businesses.",
  DISTRIBUTE: "Represent or distribute an existing international product locally.",
  SERVICE: "Provide implementation, training, integration or support around existing products.",
  ADAPT: "Apply the same underlying concept to another industry or niche.",
};

function MarketPicker({ country, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const cur = MARKETS.find((m) => m.name === country) || { flag: "🌐", name: country };
  return (
    <div className="relative inline-block" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="explore-market-selector"
        className="btn-tactile inline-flex items-center gap-2 h-10 px-3 border-2 border-ink bg-white font-semibold text-sm">
        <span>{cur.flag}</span> {cur.name} <ChevronDown size={15} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div className="absolute z-50 mt-2 w-52 bg-white border-2 border-ink shadow-[5px_5px_0_0_#0A0A0C] enter max-h-72 overflow-auto">
          {MARKETS.map((m) => (
            <button key={m.code} data-testid={`explore-market-${m.code}`} onClick={() => { onChange(m); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-bryellow/40 ${m.name === country ? "font-bold" : ""}`}>
              <span>{m.flag}</span> {m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FindingCard({ f }) {
  return (
    <div className="border-2 border-ink bg-white p-4 h-full flex flex-col" data-testid="finding-card">
      <span className={`self-start eyebrow px-2 py-0.5 ${CLASS_STYLE[f.classification] || CLASS_STYLE["Other Relevant Evidence"]}`}>{f.classification}</span>
      <h4 className="font-display font-bold text-lg leading-tight mt-2">{f.company}</h4>
      {f.location && <div className="text-xs text-ink/50 mt-0.5 inline-flex items-center gap-1"><Globe size={12} /> {f.location}</div>}
      <p className="text-sm text-ink/75 mt-2 leading-snug">{f.explanation}</p>
      {f.why_relevant && <p className="text-xs text-ink/55 mt-2"><strong>Why relevant:</strong> {f.why_relevant}</p>}
      <a href={f.source_url} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-1 mt-auto pt-3 font-semibold text-sm text-cobalt">
        View Source <ExternalLink size={13} className="arrow-move" />
      </a>
    </div>
  );
}

export default function ExploreOpportunity() {
  const { slug } = useParams();
  const { market } = useSite();
  const [country, setCountry] = useState(market.name);
  const [caseData, setCaseData] = useState(null);
  const [related, setRelated] = useState([]);
  const [exp, setExp] = useState(null);
  const [error, setError] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); getCase(slug).then(setCaseData).catch(() => {}); getRelated(slug).then(setRelated).catch(() => {}); }, [slug]);

  const begin = useCallback(async (ctry, forceRefresh) => {
    setError(false); setExp(null);
    if (pollRef.current) clearInterval(pollRef.current);
    try {
      let e = forceRefresh && forceRefresh.id ? await refreshExploration(forceRefresh.id) : await startExplore(slug, ctry);
      setExp(e);
      if (e.status === "done") return;
      pollRef.current = setInterval(async () => {
        try {
          const upd = await getExploration(e.id);
          setExp(upd);
          if (upd.status === "done" || upd.status === "error") {
            clearInterval(pollRef.current);
            if (upd.status === "error") setError(true);
          }
        } catch (err) { clearInterval(pollRef.current); setError(true); }
      }, 2500);
    } catch (err) { setError(true); }
  }, [slug]);

  useEffect(() => { begin(country); return () => pollRef.current && clearInterval(pollRef.current); }, [slug]); // eslint-disable-line

  const changeCountry = (m) => { setCountry(m.name); begin(m.name); };

  const loading = exp && exp.status === "running";
  const done = exp && exp.status === "done";
  const curStageIdx = loading ? STAGE_ORDER.indexOf(exp.stage) : -1;

  const grouped = {};
  if (done) (exp.findings || []).forEach((f) => { (grouped[f.classification] = grouped[f.classification] || []).push(f); });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to={`/case/${slug}`} className="group inline-flex items-center gap-2 font-semibold text-sm text-ink/70">
        <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> Back to case
      </Link>

      {/* Header */}
      <div className="mt-6">
        <p className="eyebrow text-ink/60">Explore Opportunity</p>
        {caseData && <p className="text-sm text-ink/50 mt-1">Original case: <Link to={`/case/${slug}`} className="font-semibold text-ink hover:text-cobalt">{caseData.headline}</Link></p>}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <h1 data-testid="explore-title" className="font-display font-extrabold text-4xl sm:text-5xl display-tight uppercase">Could this work in {country}?</h1>
        </div>
        <div className="mt-4"><MarketPicker country={country} onChange={changeCountry} /></div>
      </div>

      {/* What we're looking for */}
      <div className="mt-8 bg-ink text-white border-2 border-ink p-6">
        <p className="eyebrow text-white/60 inline-flex items-center gap-2"><Search size={13} /> What we're looking for</p>
        <p className="text-lg mt-2 leading-relaxed" data-testid="opportunity-concept">{exp?.concept || caseData?.opportunity_concept || "Identifying the underlying transferable idea…"}</p>
      </div>

      {/* Loading / progressive stages */}
      {loading && (
        <div className="mt-8 border-2 border-ink p-7" data-testid="explore-progress">
          <div className="flex items-center gap-2 text-cobalt font-semibold"><Loader2 size={18} className="animate-spin" /> Researching live sources…</div>
          <ul className="mt-5 space-y-2">
            {STAGE_ORDER.map((s, i) => (
              <li key={s} className={`flex items-center gap-3 text-sm ${i <= curStageIdx ? "text-ink font-semibold" : "text-ink/35"}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${i < curStageIdx ? "bg-brlime" : i === curStageIdx ? "bg-cobalt animate-pulse" : "bg-ink/20"}`} />
                {STAGES[s]}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-8 border-2 border-ink p-7 text-center" data-testid="explore-error">
          <AlertCircle size={28} className="mx-auto text-brorange" />
          <h3 className="font-display font-extrabold text-xl mt-2">We couldn't complete the market search right now.</h3>
          <p className="text-ink/60 text-sm mt-1">Live search didn't return usable results. We won't guess from memory.</p>
          <button onClick={() => begin(country)} data-testid="explore-retry" className="btn-tactile mt-4 inline-flex items-center gap-2 bg-ink text-white font-bold px-5 h-11 hover:bg-cobalt">Try Again</button>
        </div>
      )}

      {/* Results */}
      {done && (
        <>
          {/* result state banner */}
          <Reveal className="mt-8">
            <div className="border-2 border-ink p-6 bg-bryellow" data-testid="result-state">
              <p className="eyebrow text-ink/60">What we found</p>
              <div className="flex flex-wrap items-baseline gap-3 mt-1">
                <span className="font-display font-extrabold text-3xl">{exp.relevant_found} relevant {exp.relevant_found === 1 ? "result" : "results"} found</span>
                <span className="eyebrow bg-ink text-white px-2 py-1">{STATE_COPY[exp.result_state]?.t || exp.result_state}</span>
              </div>
              <p className="text-sm text-ink/70 mt-2">{STATE_COPY[exp.result_state]?.d}</p>
            </div>
          </Reveal>

          {/* findings grouped */}
          {exp.relevant_found > 0 ? (
            <div className="mt-6 space-y-8">
              {CLASS_ORDER.filter((cls) => grouped[cls]?.length).map((cls) => (
                <div key={cls}>
                  <h3 className="font-display font-extrabold text-lg uppercase mb-3 flex items-center gap-2">
                    <span className={`inline-block h-3 w-3 ${CLASS_STYLE[cls].split(" ")[0]}`} /> {cls} <span className="text-ink/40 font-body font-normal text-sm">({grouped[cls].length})</span>
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {grouped[cls].map((f, i) => <FindingCard key={i} f={f} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 border-2 border-ink p-8 text-center">
              <h3 className="font-display font-extrabold text-xl">No clear local evidence surfaced.</h3>
              <p className="text-ink/60 text-sm mt-1">Our search found limited public evidence of this specific solution in {country}. That does not prove it doesn't exist.</p>
            </div>
          )}

          {/* AI YAI YAI interpretation */}
          {exp.summary && (
            <Reveal className="mt-10">
              <div className="border-2 border-ink p-7 bg-brlime">
                <p className="eyebrow text-ink/60">What does the evidence suggest?</p>
                <div className="mt-3 space-y-3 text-ink/85 leading-relaxed" data-testid="evidence-summary">
                  {exp.summary.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            </Reveal>
          )}

          {/* approaches */}
          {exp.approaches?.length > 0 && (
            <div className="mt-10">
              <h3 className="font-display font-extrabold text-lg uppercase">Ways this could be approached</h3>
              <p className="text-xs text-ink/50 mb-3">Conceptual paths only — not recommendations.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {exp.approaches.map((a) => (
                  <div key={a} className="border-2 border-ink p-4">
                    <div className="font-display font-bold">{a}</div>
                    <div className="text-sm text-ink/70 mt-1">{APPROACH_COPY[a]}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* how we searched */}
          <div className="mt-10 border-2 border-ink">
            <button onClick={() => setHowOpen((o) => !o)} data-testid="how-we-searched-toggle" className="w-full flex items-center justify-between p-4 font-semibold">
              <span className="eyebrow">How we searched</span>
              <ChevronDown size={16} className={howOpen ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {howOpen && (
              <div className="px-4 pb-4 text-sm text-ink/70 space-y-1 border-t border-ink/10 pt-3">
                <div>Market searched: <strong className="text-ink">{country}</strong></div>
                <div>Opportunity concept: <strong className="text-ink">{exp.concept}</strong></div>
                <div>Search date: <strong className="text-ink">{exp.search_date ? new Date(exp.search_date).toLocaleString() : "—"}</strong></div>
                <div>Searches performed: <strong className="text-ink">{exp.searches_performed}</strong></div>
                <div>Pages/results reviewed: <strong className="text-ink">{exp.pages_reviewed}</strong></div>
                <div>Relevant results found: <strong className="text-ink">{exp.relevant_found}</strong></div>
                <div className="pt-2 text-ink/50">Queries: {exp.queries?.join(" · ")}</div>
              </div>
            )}
          </div>

          {/* freshness + refresh */}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-ink/60">
            <span>Market checked: <strong className="text-ink">{exp.search_date ? new Date(exp.search_date).toLocaleDateString() : "—"}</strong></span>
            <button onClick={() => begin(country, { id: exp.id })} data-testid="refresh-market-search" className="group btn-tactile inline-flex items-center gap-1.5 border-2 border-ink px-3 h-9 font-semibold text-xs hover:bg-secondary">
              <RefreshCw size={13} /> Refresh Market Search
            </button>
          </div>
        </>
      )}

      {/* What's happening elsewhere */}
      <div className="mt-14">
        <h3 className="font-display font-extrabold text-2xl uppercase">What's happening elsewhere</h3>
        <p className="text-sm text-ink/50 mt-1">The transformation that triggered this search, and related cases from the AI YAI YAI database.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {caseData && (
            <Link to={`/case/${caseData.slug}`} className="group lift-card border-2 border-ink bg-ink text-white p-4 block">
              <span className="eyebrow bg-white text-ink px-2 py-0.5">{caseData.country} · Original</span>
              <h4 className="font-display font-bold text-base leading-tight mt-2">{caseData.headline}</h4>
              <span className="inline-flex items-center gap-1 text-sm mt-2 font-semibold">Read case <ArrowRight size={14} className="arrow-move" /></span>
            </Link>
          )}
          {related.filter((r) => r.slug !== slug).slice(0, 5).map((r) => (
            <Link key={r.slug} to={`/case/${r.slug}`} className="group lift-card border-2 border-ink bg-white p-4 block">
              <span className="eyebrow text-ink/50">{r.industry} · {r.country}</span>
              <h4 className="font-display font-bold text-base leading-tight mt-1">{r.headline}</h4>
              <span className="inline-flex items-center gap-1 text-sm mt-2 font-semibold text-cobalt">Read case <ArrowRight size={14} className="arrow-move" /></span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
